import { createFileRoute } from "@tanstack/react-router";
import { DriverLandingPage, type DriverLandingContent } from "@/components/DriverLandingPage";
import { seoHead, localBusinessSchema, complianceToBusinessOpts, serviceSchema } from "@/lib/seo";
import { faqSchema } from "@/components/FAQAccordion";
import { getPublicConfig } from "@/lib/site-content.functions";

const CONTENT: DriverLandingContent = {
  eyebrow: "Driver & Fleet Garage Support",
  h1: "Garage Support for Uber, Bolt & High-Mileage Drivers",
  intro:
    "Working drivers can't afford an unreliable car. Woodseer Autos offers value-for-money MOT, servicing, diagnostics, brakes, tyres and repair support for Uber, Bolt, delivery and high-mileage drivers across East London — with clear advice before any work starts.",
  trustChips: [
    "DVSA-authorised MOT station V106376",
    "Classes 1, 2, 3, 4, 5 & 7",
    "Independent — no dealer mark-up",
    "Honest written quotes",
    "Mon–Sat 9am–6pm",
  ],
  audienceTitle: "Built for working drivers",
  audience: [
    "Uber and Bolt drivers",
    "Delivery drivers and couriers",
    "Private-hire and minicab drivers",
    "High-mileage commuters",
    "Local fleet and work vans",
    "Taxi drivers",
  ],
  helpTitle: "What we cover under one roof",
  helpItems: [
    "MOT testing and pre-MOT checks",
    "Interim, full and major servicing",
    "Diagnostics for warning lights",
    "Brake pads, discs and calipers",
    "Tyres, fitting, balancing and alignment",
    "Clutch, gearbox and suspension repairs",
    "Bodywork, scratches and scuffs",
    "Battery, starter and charging checks",
  ],
  issuesTitle: "Common workhorse vehicle issues",
  issues: [
    "Front tyres wearing in 8–12 weeks",
    "Brake pads needing replacement every 3–6 months",
    "Suspension knocks from speed humps and potholes",
    "Battery struggling after lots of short stop-start trips",
    "Service intervals creeping past on busy weeks",
    "Engine management lights mid-shift",
    "Warning lights triggered by low coolant or oil",
    "Cosmetic damage from tight East London streets",
  ],
  valueTitle: "How to reduce downtime",
  valueBody:
    "The cheapest repair is usually the one done early. We'll triage warning lights, weak brakes and uneven tyres before they take you off the road for a day. Book early, ask for honest advisories at every visit, and we'll help you plan repairs around your shift pattern.",
  beforeTitle: "Weekly driver check (5 minutes)",
  beforeItems: [
    "Tyre tread and pressures on all four corners",
    "All lights working — dipped, main beam, indicators, brakes",
    "Brake pedal feel and any new noises",
    "Engine oil, coolant and screenwash levels",
    "Dashboard warning lights",
    "Wiper blades and washer jets",
    "Any new vibrations, knocks or pulls",
    "Service interval vs miles driven",
  ],
  faqs: [
    { q: "Do you look after working drivers?", a: "Yes. A big share of our weekly bookings are private-hire, Uber, Bolt and delivery drivers. We understand downtime costs you money and we'll communicate clearly throughout." },
    { q: "Can I book in for a quick MOT?", a: "Yes — call us with your reg and we'll find the next slot. Most MOTs are completed within the hour." },
    { q: "Can you fit OE-quality parts at fair prices?", a: "Yes. We offer good, better and best options on most parts and explain the trade-offs. No dealer mark-up." },
    { q: "Can you handle high-mileage servicing?", a: "Yes — interim, full and major services for daily-driven and high-mileage vehicles. Service book stamped." },
    { q: "Do you offer warranty on repairs?", a: "Yes — workmanship warranty available on qualifying repairs. Ask when you book." },
    { q: "Will I be told before any extra work is done?", a: "Always. No work is carried out without your sign-off — written quote, plain English, no surprises." },
  ],
  bookingContext: "high-mileage-driver",
  waPrefill: "I drive for work / cover high mileage and need advice on servicing or repairs. Reg: ",
  relatedLinks: [
    { to: "/pco-mot-east-london", label: "PCO / private-hire MOT" },
    { to: "/mot", label: "MOT testing" },
    { to: "/servicing", label: "Servicing" },
    { to: "/diagnostics", label: "Diagnostics" },
    { to: "/mechanical-repairs", label: "Mechanical repairs" },
    { to: "/cost/full-service-east-london", label: "Full service cost" },
    { to: "/cost/brake-pads-discs-east-london", label: "Brake cost guide" },
    { to: "/value-for-money-garage-east-london", label: "Value-for-money garage" },
  ],
};

export const Route = createFileRoute("/garage-for-uber-bolt-drivers-east-london")({
  loader: async () => {
    const cfg = await getPublicConfig().catch(() => null);
    return { compliance: cfg?.compliance || null };
  },
  head: ({ loaderData }) =>
    seoHead({
      path: "/garage-for-uber-bolt-drivers-east-london",
      title: "Garage for Uber, Bolt & High-Mileage Drivers East London",
      description:
        "Value-for-money MOT, servicing, diagnostics, brakes, tyres and repair support for Uber, Bolt, delivery and high-mileage drivers in East London.",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Uber & Bolt Driver Garage", path: "/garage-for-uber-bolt-drivers-east-london" },
      ],
      jsonLd: [
        localBusinessSchema(complianceToBusinessOpts(loaderData?.compliance)),
        serviceSchema({
          name: "Garage Support for Uber, Bolt & High-Mileage Drivers",
          serviceType: "Automotive Servicing & Repair",
          description:
            "MOT testing, servicing, diagnostics and repairs for Uber, Bolt, delivery and high-mileage drivers in East London.",
          areaServed: "East London, E14",
          url: "https://woodseerautos.co.uk/garage-for-uber-bolt-drivers-east-london",
        }),
        faqSchema(CONTENT.faqs) as Record<string, unknown>,
      ],
    }),
  component: () => (
    <DriverLandingPage content={CONTENT} path="/garage-for-uber-bolt-drivers-east-london" />
  ),
});
